import type { ClinicSession } from "@auth/clinicAuthClient";
import {
  clearSessions,
  readSessions,
  writeSessions,
} from "@auth/sessionStorage";
import { create } from "zustand";

interface SessionState {
  clinic: ClinicSession | null;
  user: ClinicSession | null;
  setClinic: (next: ClinicSession | null) => void;
  setUser: (next: ClinicSession | null) => void;
  rotate: (next: ClinicSession) => void;
  clearAll: () => void;
}

const initial = readSessions();

export const useSessionStore = create<SessionState>((set) => ({
  clinic: initial.clinic,
  user: initial.user,
  setClinic: (next) => set({ clinic: next }),
  setUser: (next) => set({ user: next }),
  rotate: (next) =>
    set(next.scope === "user" ? { user: next } : { clinic: next }),
  clearAll: () => set({ clinic: null, user: null }),
}));

useSessionStore.subscribe(({ clinic, user }) => {
  if (clinic || user) writeSessions({ clinic, user });
  else clearSessions();
});

export const getActiveSession = (): ClinicSession | null => {
  const { user, clinic } = useSessionStore.getState();
  return user ?? clinic;
};

/** Namespaces per-user client state; a clinic-only session is its own principal (`-`). */
export const principalKeyOf = (session: ClinicSession | null): string =>
  session ? `${session.clinicUuid}:${session.userUuid ?? "-"}` : "anon";

export const usePrincipalKey = (): string =>
  useSessionStore((s) => principalKeyOf(s.user ?? s.clinic));

/** Cache namespace so one user's data never renders under another's session. */
export const useSessionKey = (): string =>
  useSessionStore((s) => s.user?.userUuid ?? "");
